import type { Job } from "../types";
import { jobBoundaryAcres } from "./jobAreaStats";

/** Heatmap attribute + weight math. The weights are ported from MC's HeatmapAttributeCalculator
 *  (design §3.6): percentile-based normalization, clipped here at the 1st/99th. The histogram is the board's
 *  own: equal-width bins over the same band, so each bin sits where its jobs fall on the colour
 *  scale, and a brushed run of bins narrows the jobs the layer draws. */

export type HeatmapAttributeKey = "daysLate" | "boundaryAcres";

export const HEATMAP_ATTRIBUTE_LABELS: Record<HeatmapAttributeKey, string> = {
	daysLate: "Days Late",
	boundaryAcres: "Boundary Acres",
};

/** A job's raw value for the attribute. MC parity: Days Late excludes zero/missing
 *  (`get('Late (days)') || undefined`) and keeps jobs not yet due, which "Late (days)" counts
 *  down to the due date (below 0). Boundary Acres always yields a number (zero included) —
 *  here geometry-first via jobBoundaryAcres, like the area stats. */
export function heatmapValue(job: Job, attribute: HeatmapAttributeKey): number | undefined {
	if (attribute === "daysLate") return job.daysLate || undefined;
	return jobBoundaryAcres(job);
}

/** Whether the attribute's values sit either side of 0 and are coloured that way: Days Late,
 *  where below 0 is days until due. */
export function isSignedHeatmapAttribute(attribute: HeatmapAttributeKey): boolean {
	return attribute === "daysLate";
}

/** Linear-interpolated percentile of a pre-sorted ascending array (MC calculatePercentile). */
function percentileOf(sortedValues: readonly number[], percentile: number): number {
	const index = (percentile / 100) * (sortedValues.length - 1);
	const lower = Math.floor(index);
	const upper = Math.ceil(index);
	const weight = index - lower;
	if (lower === upper) return sortedValues[lower];
	// Lerp as a + (b − a)·t, exact when a = b — the a·(1 − t) + b·t form leaves float dust there.
	return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * weight;
}

interface HeatmapScale {
	lower: number;
	upper: number;
}

/** The scale — the colours and the histogram's span alike — runs from the 1st to the 99th
 *  percentile: a few outliers can't wash every other job out to pale, and the tails still get
 *  bars of their own, so the colours spread evenly across the bars. (MC clipped at the 5th/95th.) */
const CLIP_PERCENTILE = 1;

/** The band between the `clip`th and (100 − `clip`)th percentile, or min–max when that band
 *  collapses. Null for no values; `lower === upper` only when every value is the same. */
function heatmapScale(values: readonly number[], clip = CLIP_PERCENTILE): HeatmapScale | null {
	if (values.length === 0) return null;
	const sorted = [...values].sort((a, b) => a - b);
	const minValue = sorted[0];
	const maxValue = sorted[sorted.length - 1];
	const low = percentileOf(sorted, clip);
	const high = percentileOf(sorted, 100 - clip);
	return high - low > 0 ? { lower: low, upper: high } : { lower: minValue, upper: maxValue };
}

/** Where a value sits on the scale, 0..1, clipped at both ends. */
function weightOn(scale: HeatmapScale, value: number): number {
	const clipped = Math.max(scale.lower, Math.min(scale.upper, value));
	return (clipped - scale.lower) / (scale.upper - scale.lower);
}

/** A value's weight on the scale. Unsigned (MC rules): 0..1 over the band, 0.5 each when every
 *  value is the same. Signed: -1..1 with 0 at 0, each side scaled to its own end of the band, so a
 *  job's weight says how far it is from 0 and on which side. */
function weigher(scale: HeatmapScale, signed: boolean): (value: number) => number {
	if (signed) {
		const high = Math.max(scale.upper, 0);
		const low = Math.max(-scale.lower, 0);
		return (value) => {
			if (value > 0) return high > 0 ? Math.min(value, high) / high : 1;
			if (value < 0) return low > 0 ? -Math.min(-value, low) / low : -1;
			return 0;
		};
	}
	if (scale.lower === scale.upper) return () => 0.5;
	return (value) => weightOn(scale, value);
}

/** Normalize raw values to weights, order-preserving: [0, 1] over the 1st–99th percentile
 *  band (min/max when that band collapses), clipped — or, `signed`, [-1, 1] with 0 at 0. */
export function heatmapWeights(values: readonly number[], signed = false): number[] {
	const scale = heatmapScale(values);
	return scale ? values.map(weigher(scale, signed)) : [];
}

export interface HeatmapBin {
	count: number;
	/** The bin's edges. The first bin also counts every value below its `minValue`, the last
	 *  every value above its `maxValue`. */
	minValue: number;
	maxValue: number;
	/** The weight of the bin's middle, as heatmapWeights gives it — the colour to draw it in. */
	weight: number;
}

/** The step multipliers a bar may be wide: 1, 2, 2.5, 4, 5 or 8 × 10ⁿ. Finer than the usual
 *  1 / 2 / 5 series, so the bar count can grow a little without doubling; edges still read as
 *  round numbers (0, 8, 16, 24 …). */
const NICE_FRACTIONS = [1, 2, 2.5, 4, 5, 8, 10];

/** The smallest nice step (see NICE_FRACTIONS) at least `raw`. */
function niceStep(raw: number): number {
	const magnitude = 10 ** Math.floor(Math.log10(raw));
	const fraction = raw / magnitude;
	return (NICE_FRACTIONS.find((f) => fraction <= f) ?? 10) * magnitude;
}

/** Drop floating-point dust from an edge (0.30000000000000004 → 0.3). */
const tidy = (edge: number) => Number(edge.toPrecision(12));

/** Equal-width histogram bins over the scale, on a round step, at most `maxBins + 1` of them.
 *  Values outside the scale land in the first or last bin. The step is the smallest nice step
 *  (1 / 2 / 2.5 / 4 / 5 / 8 × 10ⁿ) at least the scale's span over `maxBins`. Edges are multiples
 *  of the step, so 0 is an edge whenever the scale crosses it. Degenerate input (empty, or every
 *  value the same) → [], which the panel shows as nothing to plot. */
export function heatmapBins(values: readonly number[], maxBins: number, signed = false): HeatmapBin[] {
	const scale = heatmapScale(values);
	if (!scale || scale.lower === scale.upper) return [];
	const weightOf = weigher(scale, signed);

	const step = niceStep((scale.upper - scale.lower) / maxBins);
	const start = Math.floor(scale.lower / step) * step;
	const binCount = Math.max(1, Math.ceil((scale.upper - start) / step));
	const bins: HeatmapBin[] = Array.from({ length: binCount }, (_, i) => ({
		count: 0,
		minValue: tidy(start + i * step),
		maxValue: tidy(start + (i + 1) * step),
		weight: weightOf(start + (i + 0.5) * step),
	}));
	for (const value of values) {
		const index = Math.floor((value - start) / step);
		bins[Math.max(0, Math.min(binCount - 1, index))].count++;
	}
	return bins;
}

/** A brushed span of the histogram: values from `from` (inclusive) up to `to` (exclusive).
 *  A null end is open, so the first and last bins keep their outliers. */
export interface HeatmapRange {
	from: number | null;
	to: number | null;
}

/** The range the heatmap starts from: late jobs only for Days Late (jobs not yet due are a drag
 *  of the brush away), every value otherwise. */
export function heatmapDefaultRange(attribute: HeatmapAttributeKey): HeatmapRange {
	return isSignedHeatmapAttribute(attribute) ? { from: 0, to: null } : { from: null, to: null };
}

/** The range covering bins `first` through `last` (inclusive, in either order). */
export function heatmapRangeOfBins(bins: readonly HeatmapBin[], first: number, last: number): HeatmapRange {
	const lo = Math.max(0, Math.min(first, last));
	const hi = Math.min(bins.length - 1, Math.max(first, last));
	return {
		from: lo === 0 ? null : bins[lo].minValue,
		to: hi === bins.length - 1 ? null : bins[hi].maxValue,
	};
}

/** The range the map draws: the one the histogram shows, snapped outward to whole bins (see
 *  `heatmapBinsInRange`), so the map draws the jobs the summary counts. With no bins (every value
 *  the same) there is no brush to change a range with, so every job is drawn. */
export function heatmapDrawnRange(bins: readonly HeatmapBin[], range: HeatmapRange): HeatmapRange {
	if (bins.length === 0) return { from: null, to: null };
	const [lo, hi] = heatmapBinsInRange(bins, range);
	return lo > hi ? range : heatmapRangeOfBins(bins, lo, hi);
}

/** The first and last bin a range covers. A range whose edges no longer fall on these bins (the
 *  board's jobs changed under it) snaps outward to the bins that hold its edges. A range starting
 *  above every bin (late jobs only, on a board with none) covers none: `[bins.length, last]`. */
export function heatmapBinsInRange(bins: readonly HeatmapBin[], range: HeatmapRange): [number, number] {
	const last = bins.length - 1;
	const { from, to } = range;
	const firstIn = from === null ? 0 : bins.findIndex((b) => b.maxValue > from);
	if (firstIn === -1) return [bins.length, last];
	let hi = last;
	if (to !== null) {
		const after = bins.findIndex((b) => b.minValue >= to);
		hi = after === -1 ? last : Math.max(0, after - 1);
	}
	return [firstIn, Math.max(firstIn, hi)];
}
